const { mnemonicGenerate, mnemonicValidate, blake2AsHex } = require('@polkadot/util-crypto');
const { initKeyring } = require('./config.js');
const { buildConnection } = require('./connection.js');
const utils = require('./utils.js');

const IDENTIFIER_PREFIX = 'did:ssid:';
const IDENTIFIER_MAX_LENGTH = 32;
const IDENTIFIER_MIN_LENGTH = 3;
const DID_HEX_LEN = 64;


/** Generate Mnemonic
 * @returns {String} Mnemonic
 */
const generateMnemonic = () => mnemonicGenerate();

const checkIdentifierFormat = (identifier) => {
  const format = /^[0-9a-zA-Z]+$/;

  return format.test(identifier);
};
/**
 * Checks if the given did is in hex format or not & converts it into valid hex format.
 *
 * Note: This util function is needed since dependant module wont convert the utf
 * did to hex anymore
 *
 * @param {String} did
 * @return {String} Hex did
 */

const sanitiseDid = (did) => {
  if (did.startsWith('0x')) {
    // already hex string
    return did.padEnd(DID_HEX_LEN, '0');
  }
  // console.log('Converting to hex');
  let hexDid = Buffer.from(did, 'utf8').toString('hex');
  hexDid = '0x' + hexDid.padEnd(DID_HEX_LEN, '0');
  return hexDid;
};


/**
 * This function will find the region from the DID
 * @param {String} did
 */
function findRegionFromDid(did) {
  // Split the identifier by :
  const didParts = did.split(':');

  const region = didParts[1] || '';

  switch (region) {
    case 'yidindji':
      return 'SYD';
    case 'co2network':
      return 'CO2';
    case 'unzn':
      return 'CO2';
    default:
      return 'MMUISSID';
  }
}

/**
 * Stores the generated DID object on the blockchain.
 * @param {Object} params - The params object.
 * @param {Object} params.didDetails - DID object to be stored, generated using
 *                                      generateDID function.
 * @param {Object} params.signingKeypair - The keypair used for signing the transaction.
 * @param {String} params.appId - The optional application ID.
 * @param {ApiPromise} api - The API object for blockchain interaction.
 * @returns {String} The transaction ID for the storage operation.
 */
function storeDIDOnChain({ didDetails, signingKeypair, appId = null }, api = false) {
  return new Promise(async (resolve, reject) => {
    try {
      const provider = api || (await buildConnection('local'));

      let tx;
      if (appId === null) {
        tx = provider.tx.did.add(
          didDetails.public_key,
          sanitiseDid(didDetails.identity),
          didDetails.metadata,
        );
      } else {
        tx = provider.tx.did.addByApp(
          didDetails.public_key,
          sanitiseDid(didDetails.identity),
          didDetails.metadata,
          utils.encodeData(appId.padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes')
        );
      }

      const nonce = await provider.rpc.system.accountNextIndex(signingKeypair.address);
      const signedTx = tx.sign(signingKeypair, { nonce });
      await signedTx.send(function ({ status, dispatchError }) {
        console.log('Transaction status:', status.type);
        if (dispatchError) {
          if (dispatchError.isModule) {
            // for module errors, we have the section indexed, lookup
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { documentation, name, section } = decoded;
            reject(new Error(`${section}.${name}`));
          } else {
            // Other, CannotLookup, BadOrigin, no extra info
            reject(new Error(dispatchError.toString()));
          }
        } else if (status.isFinalized) {
          resolve(signedTx.hash.toHex());
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Stores the generated DID object on the blockchain.
 * @param {Object} params - The params object.
 * @param {Object} params.didDetails - DID object to be stored, generated using 
 *                                     generateDID function.
 * @param {Object} params.signingKeypair - The keypair used for signing the transaction.
 * @param {String} params.appId - The optional application ID.
 * @param {ApiPromise} api - The API object for blockchain interaction.
 * @returns {String} The transaction ID for the storage operation.
 */
function storePublicDID({
  didDetails,
  signingKeypair,
  appId = null,
  registrationNumber = null,
  entityName = null,
}, api = false,
) {
  return new Promise(async (resolve, reject) => {
    try {
      const provider = api || (await buildConnection('local'));

      let tx;
      if (registrationNumber === null || entityName === null) {
        reject(new Error('registrationNumber and entityName are required for Public DIDs'));
      }
      if (appId === null) {
        tx = provider.tx.did.addPublicDid(
          didDetails.public_key,
          sanitiseDid(didDetails.identity),
          didDetails.metadata,
          registrationNumber,
          entityName,
        );
      } else {
        tx = provider.tx.did.addPublicDidByApp(
          didDetails.public_key,
          sanitiseDid(didDetails.identity),
          didDetails.metadata,
          utils.encodeData(appId.padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes'),
          registrationNumber,
          entityName,
        );
      }

      const nonce = await provider.rpc.system.accountNextIndex(signingKeypair.address);
      const signedTx = tx.sign(signingKeypair, { nonce });
      await signedTx.send(({ status, dispatchError }) => {
        console.log('Transaction status:', status.type);
        if (dispatchError) {
          if (dispatchError.isModule) {
            // for module errors, we have the section indexed, lookup
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { name, section } = decoded;
            reject(new Error(`${section}.${name}`));
          } else {
            // Other, CannotLookup, BadOrigin, no extra info
            reject(new Error(dispatchError.toString()));
          }
        } else if (status.isFinalized) {
          resolve(signedTx.hash.toHex());
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

async function updateDidType({ identifier, didType, signingKeypair }, api = false) { 
  return new Promise(async (resolve, reject) => {
    try {
      const provider = api || (await buildConnection('local'));
      const didHex = sanitiseDid(identifier);
      const tx = provider.tx.did.updateDidType(didHex, didType);
      const nonce = await provider.rpc.system.accountNextIndex(signingKeypair.address);
      const signedTx = tx.sign(signingKeypair, { nonce });
      await signedTx.send(function ({ status, dispatchError }) {
        console.log('Transaction status:', status.type);
        if (dispatchError) {
          if (dispatchError.isModule) {
            // for module errors, we have the section indexed, lookup
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { name, section } = decoded;
            reject(new Error(`${section}.${name}`));
          } else {
            reject(new Error(dispatchError.toString()));
          }
        } else if (status.isFinalized) {
          resolve(signedTx.hash.toHex());
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Rotates the keys assigned to a DID.
 * This operation should only be called by validator accounts, otherwise it will fail.
 * @param {Object} params - The params object.
 * @param {String} params.identifier - The identifier of the DID.
 * @param {Uint8Array|HexString} params.newKey - The new key to be assigned to the DID.
 * @param {Keyring} params.signingKeypair - The keypair of a validator account for
*                                           signing the transaction.
 * @param {String} params.appId - The optional application ID.
 * @param {ApiPromise} api - The API object for blockchain interaction.
 * @returns {Promise<void>} A promise that resolves once the operation is completed.
 */
async function updateDidKey({ identifier, newKey, signingKeypair, appId = null }, api) {
  return new Promise(async (resolve, reject) => {
    try {
      const provider = api || (await buildConnection('local'));

      const didHex = sanitiseDid(identifier);
      // call the rotateKey extrinsinc

      let tx;
      if (appId === null) {
        tx = provider.tx.did.rotateKey(didHex, newKey);
      } else {
        tx = provider.tx.did.rotateKeyByApp(didHex, newKey, utils.encodeData(appId.padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes'));
      }

      const nonce = await provider.rpc.system.accountNextIndex(signingKeypair.address);
      const signedTx = tx.sign(signingKeypair, { nonce });
      await signedTx.send(function ({ status, dispatchError }) {
        console.log('Transaction status:', status.type);
        if (dispatchError) {
          if (dispatchError.isModule) {
            // for module errors, we have the section indexed, lookup
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { documentation, name, section } = decoded;
            reject(new Error(`${section}.${name}`));
          } else {
            reject(new Error(dispatchError.toString()));
          }
        } else if (status.isFinalized) {
          resolve(signedTx.hash.toHex());
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Store the generated DID object in blockchain
 * @param {String} vcHex VC Hex String
 * @param {KeyPair} signingKeypair Owner Key Ring pair
 * @param {ApiPromise} api (optional)
 * @returns {Promise<String>} Promise that resolves to txnId for storage operation.
 */
function addPublicKey(vcHex, signingKeypair, api = false) {
  return new Promise(async (resolve, reject) => {
    try {
      const provider = api || (await buildConnection('local'));

      const tx = provider.tx.did.addPublicKey(vcHex);

      let nonce = await provider.rpc.system.accountNextIndex(signingKeypair.address);
      let signedTx = tx.sign(signingKeypair, { nonce });
      await signedTx.send(function ({ status, dispatchError }) {
        console.log('Transaction status:', status.type);
        if (dispatchError) {
          if (dispatchError.isModule) {
            // for module errors, we have the section indexed, lookup
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { documentation, name, section } = decoded;
            // console.log(`${section}.${name}: ${documentation.join(' ')}`);
            reject(new Error(`${section}.${name}`));
          } else {
            // Other, CannotLookup, BadOrigin, no extra info
            // console.log(dispatchError.toString());
            reject(new Error(dispatchError.toString()));
          }
        } else if (status.isFinalized) {
          // console.log('Finalized block hash', status.asFinalized.toHex());
          resolve(signedTx.hash.toHex());
        }
      });
    } catch (err) {
      // console.log(err);
      reject(err);
    }
  });
}

/**
 *
 * @param {String} identifier
 * @param {String} metadata
 * @param {KeyringObj} signingKeypair of a validator account
 * @param {ApiPromise} api
 * @returns {Promise<String>} Promise that resolves to txnId for storage operation.
 */
async function updateMetadata(identifier, metadata, signingKeypair, api = false) {
  return new Promise(async (resolve, reject) => {
    try {
      const provider = api || (await buildConnection('local'));
      const did_hex = sanitiseDid(identifier);
      const tx = provider.tx.did.updateMetadata(did_hex, metadata);
      let nonce = await provider.rpc.system.accountNextIndex(signingKeypair.address);
      let signedTx = tx.sign(signingKeypair, { nonce });
      await signedTx.send(function ({ status, dispatchError }) {
        console.log('Transaction status:', status.type);
        if (dispatchError) {
          if (dispatchError.isModule) {
            // for module errors, we have the section indexed, lookup
            const decoded = api.registry.findMetaError(dispatchError.asModule);
            const { documentation, name, section } = decoded;
            // console.log(`${section}.${name}: ${documentation.join(' ')}`);
            reject(new Error(`${section}.${name}`));
          } else {
            // Other, CannotLookup, BadOrigin, no extra info
            // console.log(dispatchError.toString());
            reject(new Error(dispatchError.toString()));
          }
        } else if (status.isFinalized) {
          // console.log('Finalized block hash', status.asFinalized.toHex());
          resolve(signedTx.hash.toHex())
        }
      });
    } catch (err) {
      // console.log(err);
      reject(err);
    }
  });
}


// DID Query Functions

/**
 * Get did information from accountID
 * @param {String} identifier DID Identifier
 * @returns {JSON}
 */
async function getDIDDetails(identifier, api = false) {
  try {
    const provider = api || (await buildConnection('local'));
    const did_hex = sanitiseDid(identifier);
    const palletVersion = await getDidPalletVersion(provider);
    let data;
    if (palletVersion === null) {
      data = (await provider.query.did.dIDs(did_hex)).toJSON();
      return {
        identifier: data[0].identifier,
        public_key: data[0].public_key,
        metadata: data[0].metadata,
        added_block: data[1],
      };
    }
    else {
      data = (await provider.query.did.didDetails(did_hex)).toHuman();
      return {
        identifier: data.identifier,
        public_keys: data.public_keys,
        metadata: data.metadata,
      };
    }
  } catch (error) {
    throw Error('Failed to fetch details');
  }
}

/**
 * Get did information from Did
 * @param {String} identifier DID Identifier
 * @returns {JSON}
 */
async function didLookup(identifier, api = false) {
  try {
    const provider = api || (await buildConnection('local'));
    const didHex = sanitiseDid(identifier);
    let data = (await provider.query.did.didLookup(didHex)).toHuman();
    data = data.map((item) => ({
      appId: item[0],
      accountId: item[1],
      addedOn: item[2],
    }));
    return data;
  } catch (error) {
    throw Error('Failed to fetch details');
  }
}

/**
 * Get did information from Did
 * @param {Object} params - The params object.
 * @param {String} params.identifier - DID Identifier
 * @param {String} params.appId - Application ID
 * @param {ApiPromise} api - The API connection to use, defaults to a local connection
 * @returns {JSON}
 */
async function didLookupByApp({ identifier, appId = null }, api = false) {
  try {
    const provider = api || (await buildConnection('local'));
    const didHex = sanitiseDid(identifier);
    const appIdHex = utils.encodeData((appId !== null ? appId : findRegionFromDid(identifier)).padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes');
    const didDetails = (await provider.query.did.didLookup(didHex)).toHuman();
    for (let i = 0; i < didDetails.length; i++) {
      if (didDetails[i].includes(appIdHex)) {
        return {
          appId: didDetails[i][0],
          accountId: didDetails[i][1],
          addedOn: didDetails[i][2],
        };
      }
    }
    console.log('No matching app ID found, returning first entry if available');
    if (appId == null && didDetails.length > 0) {
      return {
        appId: didDetails[0][0],
        accountId: didDetails[0][1],
        addedOn: didDetails[0][2],
      };
    }
    return null;
  } catch (error) {
    throw Error('Failed to fetch details');
  }
}


async function getDidType(identifier, provider) { 
  try {
    const didHex = sanitiseDid(identifier);
    const didType = (await provider.query.did.didType(didHex)).toHuman();
    return didType;
  } catch (error) {
    throw Error('Failed to fetch DID Type');
  }
}

/**
 * Get the block number for a given DID
 * @param {Object} params - The params object
 * @param {String} params.identifier - The DID identifier to resolve
 * @param {String} params.appId - The application ID to use for the lookup, defaults to null
 * @param {ApiPromise} api - The API connection to use, defaults to a local connection
 * @returns {Promise<String>} - The block number associated with the given DID
 */
async function getBlockNumber({ identifier, appId = null }, api = false) {
  const provider = api || (await buildConnection('local'));
  const did_hex = sanitiseDid(identifier);
  const palletVersion = await getDidPalletVersion(provider);
  let data;
  if (palletVersion === null) {
    data = await getDIDDetails(identifier, provider);
    return data.added_block;
  } else {
    const app_id_hex = utils.encodeData((appId !== null ? appId : findRegionFromDid(identifier)).padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes');
    didDetails = (await provider.query.did.didLookup(did_hex)).toJSON();
    for (let i = 0; i < didDetails.length; i++) {
      if (didDetails[i].includes(app_id_hex)) {
        return didDetails[i][2];
      }
    }
  }
}

/**
 * Get the accountId for a given DID
 * @param {Object} params - The params object
 * @param {String} params.identifier - The DID identifier to resolve
 * @param {Number} params.blockNumber - The block number to use for the lookup, defaults to null
 * @param {String} params.appId - The application ID to use for the lookup, defaults to null
 * @param {ApiPromise} api - The API connection to use, defaults to a local connection
 * @returns {String} - The account ID associated with the given DID
 */
async function resolveDIDToAccount({ identifier, blockNumber = null, appId = null }, api = false) {

  const provider = api || (await buildConnection('local'));
  const did_hex = sanitiseDid(identifier);
  const palletVersion = await getDidPalletVersion(provider);
  let data;
  if (palletVersion === null) {
    data = await resolveDIDToAccountV1(did_hex, provider, blockNumber);
  } else {
    const app_id_hex = utils.encodeData((appId !== null ? appId : findRegionFromDid(identifier)).padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes');
    data = await resolveDIDToAccountV2(did_hex, provider, blockNumber, app_id_hex);
  }

  return data;
}

/**
 * Get the accountId for a given DID
 * @param {String} identifier
 * @param {ApiPromise} api
 * @param {Number} blockNumber
 * @returns {String}
 */
async function resolveDIDToAccountV1(did_hex, provider, blockNumber) {

  if (!blockNumber && blockNumber != 0) {
    return (await provider.query.did.lookup(did_hex)).toHuman();
  }
  const didDetails = await getDIDDetails(identifier, provider);
  if (blockNumber >= didDetails.added_block) {
    return (await provider.query.did.lookup(did_hex)).toHuman();
  }
  const keyHistories = await getDidKeyHistory({ identifier: identifier }, provider);
  if (!keyHistories) {
    return null;
  }
  const keyIndex = keyHistories.reverse().findIndex((value) => blockNumber >= parseInt(value[1]));
  if (keyIndex < 0) {
    return null;
  }
  return keyHistories[keyIndex][0];
}

/**
 * Get the accountId for a given DID
 * @param {String} identifier
 * @param {ApiPromise} api
 * @param {Number} blockNumber
 * @returns {String}
 */
async function resolveDIDToAccountV2(did_hex, provider, blockNumber, app_id_hex) {
  const keyring = await initKeyring();
  if (!blockNumber && blockNumber != 0) {
    const result = (await provider.query.did.appIdLookup(did_hex, app_id_hex)).toHuman();
    if (!result){
      return null;
    }
    return keyring.encodeAddress(result, 42);
  }

  const didDetails = (await provider.query.did.didLookup(did_hex)).toJSON();

  let added_block;
  for (let i = 0; i < didDetails.length; i++) {
    if (didDetails[i].includes(app_id_hex)) {
      added_block = didDetails[i][2];
    }
  }
  if (blockNumber >= added_block) {
    const result = ((await provider.query.did.appIdLookup(did_hex, app_id_hex)).toHuman());
    return keyring.encodeAddress(result, 42);
  }
  const keyHistories = await getDidKeyHistory({ identifier: did_hex }, provider);

  if (!keyHistories) {
    return null;
  }
  const keyIndex = keyHistories.reverse().findIndex((value) => blockNumber >= parseInt(value[1]));
  if (keyIndex < 0) {
    return null;
  }
  return keyHistories[keyIndex][0];

}

/**
 * Get the DID associated to given accountID
 * @param {String} accountId (hex/base64 version works)
 * @param {ApiPromise} api
 * @returns {String | Boolean} (false if not found)
 */
async function resolveAccountIdToDid(accountId, api = false) {
  const provider = api || (await buildConnection('local'));
  const data = (await provider.query.did.rLookup(accountId)).toHuman();
  // return false if empty
  if (data === '0x0000000000000000000000000000000000000000000000000000000000000000') {
    return false;
  }
  return data;
}

/**
 * Get did information from accountID
 * @param {ApiPromise} api - The Polkadot API Promise (optional)
 * @returns {JSON|null} - The pallet version data or null if an error occurs
 */
async function getDidPalletVersion(api) {
  try {
    const provider = api || (await buildConnection('local'));
    const data = (await provider.query.did.palletVersion()).toJSON();
    return data;
  } catch (error) {
    console.error('Failed to fetch details', error);
    return null;
  }
}

/**
 * Get public key details for a given DID identifier and optional application ID
 * @param {Object} params - The params object
 * @param {String} params.identifier - The DID identifier
 * @param {String} params.appId - The application ID (optional)
 * @param {ApiPromise} api - The API connection to use, defaults to a local connection
 * @returns {JSON} - The public key details in JSON format
 * @throws {Error} - Throws an error if unable to fetch public key details
 */
async function getPublicKeyByAppId({ identifier, appId = null }, api = false) {
  try {
    const provider = api || (await buildConnection('local'));
    const did_hex = sanitiseDid(identifier);

    const app_id_hex = utils.encodeData((appId !== null ? appId : findRegionFromDid(identifier)).padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes');
    const data = (await provider.query.did.appIdLookup(did_hex, app_id_hex)).toJSON();
    return data;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to fetch public key details');
  }
}

/**
 * Check if the user is an approved validator
 * @param {String} identifier
 * @param {ApiPromise} api
 * @returns {Boolean}
 */
async function isDidValidator(identifier, api = false) {
  const provider = api || (await buildConnection('local'));
  const did_hex = sanitiseDid(identifier);
  const vList = (await provider.query.validatorSet.members()).toJSON();
  return vList.includes(did_hex);
}

/**
 * Fetch the history of rotated keys for the specified DID
 * @param {Object} params - The params object
 * @param {String} params.identifier - The DID identifier for which to fetch the key history
 * @param {String} params.appId - The application ID, defaults to null
 * @param {ApiPromise} api - The API connection to use, defaults to a local connection
 * @returns {Array} - The key history data
 * @throws {Error} - Throws an error if unable to fetch key history
 */
async function getDidKeyHistory({ identifier, appId = null }, api = false) {
  const provider = api || (await buildConnection('local'));
  const did_hex = sanitiseDid(identifier);

  const app_id_hex = utils.encodeData((appId !== null ? appId : findRegionFromDid(identifier)).padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes');

  const palletVersion = await getDidPalletVersion(provider);
  let data;
  if (palletVersion === null) {
    data = (await provider.query.did.prevKeys(did_hex)).toHuman();
  }
  else {
    data = (await provider.query.did.prevKeysByApp(did_hex, app_id_hex)).toHuman();
  }

  if (data === null) {
    throw new Error('No data found for the provided DID and app ID');
  }

  return data;
}

// Helper functions

/**
 * Generate did object to be stored in blockchain.
 * @param {String} mnemonic
 * @param {String} identifier
 * @param {String} metadata
 * @returns {Object} Object containing did structure
 */
const generateDID = async (mnemonic, identifier, metadata = '') => {
  const keyring = await initKeyring();
  const isValidIdentifier = checkIdentifierFormat(identifier);
  const isValidMnemonic = mnemonicValidate(mnemonic);
  if (!isValidMnemonic) {
    throw new Error({
      errorCode: 'notValidMnemonic',
      errorMessage: 'Not valid mnemonic supplied',
    });
  }
  if (!isValidIdentifier) {
    throw new Error({
      errorCode: 'notValidIdentifier',
      errorMessage: 'Not valid identifier supplied',
    });
  }
  if (
    (IDENTIFIER_PREFIX.length + identifier.length) > IDENTIFIER_MAX_LENGTH ||
    identifier.length < IDENTIFIER_MIN_LENGTH
  ) {
    throw new Error({
      errorCode: 'identifierLengthInvalid',
      errorMessage: 'Identifier length invalid',
    });
  }
  const pubKey = await keyring.addFromUri(mnemonic).publicKey;
  return {
    public_key: pubKey, // this is the public key linked to the did
    identity: IDENTIFIER_PREFIX + identifier, // this is the actual did
    metadata,
  };
};

/**
  * Create VC
  * @param  {Object} params 
  * @param  {String} params.appId - App ID of new public key
  * @param  {String} params.issuerAppid - App ID of issuer DID
  * @param  {HexString} params.newPublicKey - New Public Key to be added to DID
  * @param  {String} owner - DID of the owner
  * @param  {KeyPair} sigKeypair - DID Owner Key Ring pair
  * @returns {String} VC Hex String
  */
function generateAddKeyVC({ appId, issuerAppid, newPublicKey }, owner, sigKeypair) {
  let encodedVCProperty, encodedData, hash;

  let vcProperty = {
    app_id: utils.encodeData(appId.padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes'),
    issuer_app_id: utils.encodeData(issuerAppid.padEnd(utils.APP_ID_BYTES, '\0'), 'app_id_bytes'),
    new_public_key: utils.encodeData(newPublicKey, 'PublicKey'),
  };
  console.log(vcProperty);
  encodedVCProperty = utils.encodeData(vcProperty, 'AddPubKeyVCProperties')
    .padEnd((utils.VC_PROPERTY_BYTES * 2) + 2, '0');

  owner = sanitiseDid(owner);

  encodedData = utils.encodeData({
    vc_property: encodedVCProperty,
    owner,
  }, 'ADD_KEY_VC_HEX');
  hash = blake2AsHex(encodedData);

  const sign = utils.bytesToHex(sigKeypair.sign(hash));
  let vcObject = {
    hash,
    owner,
    signatures: [sign],
    vc_property: encodedVCProperty,
  };
  return utils.encodeData(vcObject, 'AddPubKeyVC');
}

function generatePaymentVC({ purpose, fees }, userDid, sigKeypair) {
  const vcProperty = {
    purpose: utils.encodeData(purpose.padEnd(32, '\0'), 'Purpose'),
    fees: utils.encodeData(fees, 'Amount'),
  };
  console.log(vcProperty);
  const encodedVCProperty = utils.encodeData(vcProperty, 'PaymentVCProperty')
    .padEnd((utils.VC_PROPERTY_BYTES * 2) + 2, '0');

  const userDidHex = sanitiseDid(userDid);
  const issuers = [userDidHex];

  const encodedData = utils.encodeData({
    vc_property: encodedVCProperty,
    owner: userDidHex,
  }, 'VC_HEX');
  const hash = blake2AsHex(encodedData);

  const sign = utils.bytesToHex(sigKeypair.sign(hash));
  const vcObject = {
    hash,
    owner: sanitiseDid('did:ssid:issuer'),
    issuers: [userDidHex],
    is_vc_used: false,
    vc_type: 'PaymentConsentVC',
    signatures: [sign],
    vc_property: encodedVCProperty,
  };
  return utils.encodeData(vcObject, 'PaymentVC');
}


/**
 * Convert to hex but return fixed size always, mimics substrate storage
 * @param {String} data
 * @param {Int} size
 * @return {String}
 */
function convertFixedSizeHex(data, size = 64) {
  if (data.length > size) throw new Error('Invalid Data');
  const identifierHex = Buffer.from(data).toString('hex');
  return `0x${identifierHex.padEnd(size, '0')}`;
}


module.exports = {
  generateMnemonic,
  generateDID,
  storeDIDOnChain,
  getDIDDetails,
  updateDidKey,
  resolveDIDToAccount,
  getDidKeyHistory,
  resolveAccountIdToDid,
  isDidValidator,
  updateMetadata,
  sanitiseDid,
  generateAddKeyVC,
  addPublicKey,
  findRegionFromDid,
  getPublicKeyByAppId,
  didLookup,
  didLookupByApp,
  getBlockNumber,
  getDidType,
  storePublicDID,
  updateDidType,
  generatePaymentVC,
};
